import React from "react";
import { Sparkles, AlertTriangle, Star, Plus as PlusSign, Minus } from "lucide-react";
import { PILLAR_ORDER, PILLARS, getSkill, skillsForAssessment, categoriesFor, pillarCounts, skillLabel, isLive } from "../../lib/inventorySkills";
import { PILLAR_ICONS, PILLAR_COLORS } from "./PillarPanel";
import { RATING_META } from "../../lib/inventorySkills";

// Tactical: the coach's whole-section rating (Anchor / Good / Rate Limiter), picked on each section header in Step 1. Read-only here.
function TacticalSectionTiles({ ratings, categoryRatings }) {
  const rated = categoriesFor("tactical", ratings).filter(cat => categoryRatings?.[cat]);
  if (!rated.length) return <div className="mt-4 text-[10px] text-[#575757] italic" data-testid="review-tactical-categories-empty">Rate each section as a whole on its header in Step 1 to see it here.</div>;
  return (
    <div className="mt-4" data-testid="review-tactical-categories">
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-2">Sections as a whole</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rated.map(cat => {
          const m = RATING_META[categoryRatings[cat]];
          const id = cat.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          return (
            <div key={cat} className="border bg-[#141414] px-3 py-2.5 flex items-center justify-between gap-3" style={{ borderColor: `${m.color}66` }} data-testid={`review-tactical-cat-${id}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white leading-tight">{cat}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold shrink-0" style={{ color: m.color }} data-testid={`review-tactical-cat-${id}-label`}>{m.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ANCHOR_ACCENT = "#10b981", LIMITER_ACCENT = "#f59e0b";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

// Score: +1 Anchor · 0 Good · −1 Rate Limiter. Positive = Strong, zero = Mixed, negative = At Risk.
export const ratingFor = (score, rated) => {
  if (!rated) return { key: "unrated", accent: "#575757", label: "Unrated", badge: <span className="text-lg leading-none">—</span> };
  if (score > 0) return { key: "strong", accent: "#10b981", label: "Strong", badge: <PlusSign className="w-4 h-4" strokeWidth={3} /> };
  if (score === 0) return { key: "mixed", accent: "#f59e0b", label: "Mixed", badge: <span className="text-lg leading-none font-bold">=</span> };
  return { key: "at-risk", accent: "#f87171", label: "At Risk", badge: <Minus className="w-4 h-4" strokeWidth={3} /> };
};
const POINTS = { anchor: 1, good: 0, limiter: -1 };

// Category summary tiles (Character DNA style): name · "Strong · +1" · round badge.
function CategoryTiles({ pillar, ratings }) {
  const byId = Object.fromEntries(ratings.map(r => [r.skillId, r.rating]));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4" data-testid={`review-${pillar}-categories`}>
      {categoriesFor(pillar, ratings).map(cat => {
        const vals = skillsForAssessment(pillar, ratings).filter(s => s.category === cat).map(s => byId[s.id]).filter(Boolean);
        const score = vals.reduce((a, v) => a + POINTS[v], 0); const rating = ratingFor(score, vals.length);
        const id = cat.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <div key={cat} className="border bg-[#141414] px-3 py-2.5 flex items-center gap-2.5" style={{ borderColor: `${rating.accent}66` }} data-testid={`review-${pillar}-cat-${id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white leading-tight">{cat}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] mt-1" style={{ color: rating.accent }} data-testid={`review-${pillar}-cat-${id}-label`}>{rating.label}{vals.length ? ` · ${score > 0 ? "+" : ""}${score}` : ""}</div>
            </div>
            <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full" style={{ background: `${rating.accent}22`, color: rating.accent }}>{rating.badge}</div>
          </div>
        );
      })}
    </div>
  );
}

// One column (Anchors / Rate Limiters): tinted header, starred picks as big left-bar lines, remaining items as pick chips.
function ReviewList({ label, accent, Icon, items, testid, onPrimary, canEdit }) {
  const picked = items.filter(r => r.isPrimary), rest = items.filter(r => !r.isPrimary);
  return (
    <div className="border bg-[#171717]" style={{ borderColor: `${accent}44` }} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b" style={{ borderColor: `${accent}55`, background: `${accent}14` }}>
        <div className="flex items-center gap-2"><Icon className="w-4 h-4" style={{ color: accent }} /><div className="font-heading uppercase text-sm tracking-[0.3em]" style={{ color: accent }}>{label}</div></div>
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0]" data-testid={`${testid}-count`}>{picked.length}/{items.length}</div>
      </div>
      <div className="p-3 space-y-1.5">
        {!items.length && <div className="text-xs italic text-[#575757] px-3 py-4 border border-dashed border-white/10" data-testid={`${testid}-empty`}>None marked yet</div>}
        {picked.map(r => { const s = getSkill(r.skillId); return (
          <div key={r.skillId} className="relative flex items-center gap-2 border-l-4 pl-3 pr-8 py-1.5 bg-white/[0.03]" style={{ borderColor: accent }} data-testid={`${testid}-item`}>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-sm lg:text-base uppercase tracking-tight text-white leading-none truncate">{skillLabel(s)}</div>
              <div className="text-[8px] uppercase tracking-[0.25em] text-[#8a8a8a] mt-0.5" data-testid={`${testid}-item-category`}>{s.category}{r.limiterType ? <span style={{ color: accent }}> · {r.limiterType}</span> : null}</div>
            </div>
            {canEdit && <button onClick={() => onPrimary(r.skillId)} className="absolute right-2 top-1/2 -translate-y-1/2" title="Un-feature" data-testid={`${testid}-star-${r.skillId}`}>
              <Star className="w-3.5 h-3.5" style={{ color: accent }} fill={accent} />
            </button>}
          </div>
        ); })}
        {rest.length > 0 && (
          <div data-testid={`${testid}-pool`} className="space-y-1">
            {picked.length > 0 && <div className="text-[8px] uppercase tracking-[0.3em] text-[#575757] pt-1.5">All {label.toLowerCase()} · {rest.length} more</div>}
            {canEdit && picked.length === 0 && <div className="text-[8px] uppercase tracking-[0.3em] text-[#757575]">Star 2–3 to feature</div>}
            {rest.map(r => { const s = getSkill(r.skillId); return (
              <div key={r.skillId} className="relative flex items-center gap-2 border-l-2 pl-2.5 pr-7 py-1 bg-white/[0.015]" style={{ borderColor: `${accent}55` }} data-testid={`${testid}-pool-item`}>
                <div className="min-w-0 flex-1 font-heading text-[13px] uppercase tracking-tight text-[#c0c0c0] leading-none truncate">{skillLabel(s)} <span className="font-sans text-[8px] tracking-[0.2em] text-[#757575] ml-1">{s.category}</span></div>
                {canEdit && <button onClick={() => onPrimary(r.skillId)} className="absolute right-2 top-1/2 -translate-y-1/2" title="Feature this" data-testid={`${testid}-star-${r.skillId}`}>
                  <Star className="w-3 h-3 text-[#575757] hover:text-white" />
                </button>}
              </div>
            ); })}
          </div>
        )}
      </div>
    </div>
  );
}

// Step 2 · Review Anchors & Rate Limiters: one Character-DNA-style card per pillar, filled live from Step 1.
export function AssessmentReview({ assessment, onSetPrimary, onCategoryRating, canEdit }) {
  const ratings = assessment?.ratings || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="assessment-review">
      {PILLAR_ORDER.map(k => {
        const Icon = PILLAR_ICONS[k];
        const mine = ratings.filter(r => getSkill(r.skillId).pillar === k && isLive(r)); const c = pillarCounts(ratings, k);
        const anchors = mine.filter(r => r.rating === "anchor"), limiters = mine.filter(r => r.rating === "limiter"), good = mine.filter(r => r.rating === "good");
        return (
          <section key={k} className="border border-white/10 bg-gradient-to-br from-[#1c1c1c] to-[#141414] p-5 lg:p-7" data-testid={`review-${k}`}>
            <div className="flex items-center gap-3">
              <Icon className="w-6 h-6 shrink-0" style={{ color: PILLAR_COLORS[k] }} />
              <h3 className="font-heading uppercase text-2xl lg:text-3xl tracking-[0.1em] text-white leading-none">{PILLARS[k].title}</h3>
            </div>
            <div className="text-[12px] italic text-[#a0a0a0] mt-2 mb-5" data-testid={`review-${k}-stamp`}>
              Curated main points of emphasis · pulled from {assessment ? `the ${assessment.label || "latest"} assessment · ${fmtDate(assessment.created_at)} · ${c.rated} of ${c.total} rated` : "the latest assessment"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ReviewList label="Anchors" accent={ANCHOR_ACCENT} Icon={Sparkles} items={anchors} testid={`review-${k}-anchors`} onPrimary={onSetPrimary} canEdit={canEdit} />
              <ReviewList label="Rate Limiters" accent={LIMITER_ACCENT} Icon={AlertTriangle} items={limiters} testid={`review-${k}-limiters`} onPrimary={onSetPrimary} canEdit={canEdit} />
            </div>
            <div className="text-[11px] text-[#a0a0a0] mt-3" data-testid={`review-${k}-good`}><span className="text-[#60a5fa] font-bold">Good ({good.length})</span>{good.length ? `: ${good.map(r => skillLabel(getSkill(r.skillId))).join(", ")}` : ""}</div>
            {k === "tactical" ? <TacticalSectionTiles ratings={mine} categoryRatings={assessment?.category_ratings} /> : <CategoryTiles pillar={k} ratings={mine} />}
          </section>
        );
      })}
    </div>
  );
}
